import { useState } from 'react';
import { Plus, Edit2, Trash2, Building2 } from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { EntityForm } from './EntityForm';
import type { Entity } from '@perfin/shared';

export function EntitySection() {
  const { entities, accounts, removeEntity, toggleEntityStatus } = useSettingsStore();
  const toast = useToast();
  const { confirmDelete } = useConfirm();

  const [showForm, setShowForm] = useState(false);
  const [editingEntity, setEditingEntity] = useState<Entity | null>(null);

  const handleEdit = (entity: Entity) => {
    setEditingEntity(entity);
    setShowForm(true);
  };

  const handleDelete = (entity: Entity) => {
    // Check if entity has associated accounts
    const entityAccounts = accounts.filter((a) => a.entityId === entity.id);
    if (entityAccounts.length > 0) {
      toast.error(`Cannot delete "${entity.name}" - it has ${entityAccounts.length} account(s) linked to it`);
      return;
    }

    confirmDelete({
      title: 'Delete Entity',
      message: `Are you sure you want to delete "${entity.name}"? This cannot be undone.`,
      onConfirm: async () => {
        try {
          await removeEntity(entity.id);
          toast.success('Entity deleted');
        } catch {
          toast.error('Failed to delete entity');
        }
      },
    });
  };

  const handleToggleActive = async (entity: Entity) => {
    try {
      await toggleEntityStatus(entity.id, !entity.isActive);
      toast.success(entity.isActive ? 'Entity deactivated' : 'Entity activated');
    } catch {
      toast.error('Failed to update entity status');
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEntity(null);
  };

  // Get account count for each entity
  const getAccountCount = (entityId: string) => {
    return accounts.filter((a) => a.entityId === entityId).length;
  };

  // Sort entities: active first, then by name
  const sortedEntities = [...entities].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Entities</h2>
          <p className="text-sm text-gray-500 mt-1">
            Banks, institutions, and counterparties for your transactions
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Entity
        </Button>
      </div>

      <Card padding="none">
        <div className="divide-y divide-gray-200">
          {entities.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No entities yet. Add banks or institutions to organize your accounts.
            </div>
          ) : (
            sortedEntities.map((entity) => {
              const accountCount = getAccountCount(entity.id);

              return (
                <div
                  key={entity.id}
                  className={`flex items-center gap-4 p-4 ${
                    !entity.isActive ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`p-2 rounded-lg ${
                      entity.isActive ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    <Building2 className="h-5 w-5" />
                  </div>

                  {/* Name and account count */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{entity.name}</span>
                      {!entity.isActive && (
                        <Badge variant="secondary" size="sm">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {accountCount} {accountCount === 1 ? 'account' : 'accounts'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(entity)}
                      className={`px-2 py-1 text-xs rounded ${
                        entity.isActive
                          ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                    >
                      {entity.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleEdit(entity)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(entity)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      {/* Add/Edit Entity Modal */}
      {showForm && (
        <EntityForm
          entity={editingEntity}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}
